import { useState, useEffect } from 'react'
import { MapPin, UserCog, X, PencilLine, Trash2 } from 'lucide-react'
import { Card, Badge, Button, Input, Select, SearchBar, ConfirmDialog, Modal } from '../../../shared/components/UI'
import { unitsApi, usersApi } from '../../../shared/lib/api'

const emptyForm = { name: '', location: '', supervisorId: '', status: 'active' }

export function UnitsPage() {
  const [units, setUnits] = useState([])
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(null)
  const [search, setSearch] = useState('')
  const [form, setForm] = useState(emptyForm)
  const [deletingId, setDeletingId] = useState(null)
  const [acting, setActing] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    Promise.all([
      unitsApi.list(),
      usersApi.list()
    ]).then(([unitRes, userRes]) => {
      setUnits(unitRes.data.data)
      setUsers(userRes.data.data)
    }).finally(() => setLoading(false))
  }, [])

  const startEdit = (u) => {
    setEditing(u.id)
    setForm({
      name: u.name,
      location: u.location || '',
      supervisorId: u.supervisor_id || '',
      status: u.status
    })
  }

  const startNew = () => {
    setEditing('new')
    setForm(emptyForm)
  }

  const cancel = () => {
    setEditing(null)
    setForm(emptyForm)
  }

  const save = async () => {
    if (!form.name.trim()) return

    setActing(true)
    setError('')
    try {
      if (editing === 'new') {
        await unitsApi.create(form)
      } else {
        await unitsApi.update(editing, form)
      }
      const res = await unitsApi.list()
      setUnits(res.data.data)
      cancel()
    } catch (err) {
      setError(err?.response?.data?.message || 'Save failed.')
      console.error(err)
    } finally {
      setActing(false)
    }
  }

  const handleDelete = async () => {
    if (!deletingId) return
    setActing(true)
    setError('')
    try {
      await unitsApi.delete(deletingId)
      setUnits(p => p.filter(u => u.id !== deletingId))
      setDeletingId(null)
    } catch (err) {
      setError(err?.response?.data?.message || 'Delete failed.')
    } finally {
      setActing(false)
    }
  }

  const visible = units.filter((u) => {
    const query = search.toLowerCase()
    return (
      (u.name || '').toLowerCase().includes(query) ||
      (u.location || '').toLowerCase().includes(query) ||
      (u.supervisor?.name || '').toLowerCase().includes(query)
    )
  })

  const supervisorOptions = users
    .filter((u) => u.role !== 'admin')
    .map((u) => ({ label: `${u.name} (${u.role.replace('_', ' ')})`, value: u.id }))

  const activeCount = units.filter((u) => u.status === 'active').length
  const inactiveCount = units.filter((u) => u.status !== 'active').length
  const assignedSupervisors = units.filter((u) => u.supervisor_id).length

  if (loading) return <div className="p-8 text-center text-gray-400">Loading units...</div>

  return (
    <>
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-white border border-t-4 border-gray-100 border-t-brand-600 rounded-xl p-4">
              <p className="stat-label">Total Units</p>
              <p className="stat-val">{units.length}</p>
            </div>
            <div className="bg-white border border-t-4 border-gray-100 border-t-green-500 rounded-xl p-4">
              <p className="stat-label">Active Units</p>
              <p className="stat-val">{activeCount}</p>
            </div>
            <div className="bg-white border border-t-4 border-gray-100 border-t-amber-500 rounded-xl p-4">
              <p className="stat-label">Supervisors Assigned</p>
              <p className="stat-val">{assignedSupervisors}<span className="text-sm font-normal text-gray-400"> / {units.length}</span></p>
            </div>
          </div>

          <SearchBar value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by unit, location, supervisor...">
            <Button onClick={startNew}>+ Add Unit</Button>
          </SearchBar>

          <Card title={`Units (${visible.length})`} bodyClass="!p-0">
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Unit</th>
                    <th>Location</th>
                    <th>Supervisor</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.length === 0 && (
                    <tr><td colSpan={5} className="text-center py-10 text-gray-400 text-sm">No units found</td></tr>
                  )}
                  {visible.map((u) => (
                    <tr key={u.id}>
                      <td className="font-medium text-gray-800">{u.name}</td>
                      <td className="text-gray-500 text-xs">
                        <div className="inline-flex items-center gap-1.5 justify-start w-full">
                          <MapPin size={12} className="text-gray-300" />
                          <span>{u.location || '-'}</span>
                        </div>
                      </td>
                      <td className="text-gray-600 text-xs">
                        <div className="inline-flex items-center gap-1.5 justify-start w-full">
                          <UserCog size={12} className="text-gray-300" />
                          <span>{u.supervisor?.name || 'Not assigned'}</span>
                        </div>
                      </td>
                      <td>
                        <div className="flex justify-start">
                          <Badge variant={u.status === 'active' ? 'green' : 'amber'}>
                            {u.status === 'active' ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                      </td>
                      <td className="pr-4">
                        <div className="flex justify-start gap-1">
                          <button 
                            onClick={() => startEdit(u)}
                            className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-xl transition-all"
                            title="Edit"
                          >
                            <PencilLine size={16} />
                          </button>
                          <button 
                            onClick={() => setDeletingId(u.id)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all"
                            title="Delete Unit"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {units.length > 0 && inactiveCount > 0 && (
            <div className="rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 text-xs text-amber-700">
              {inactiveCount} unit{inactiveCount > 1 ? 's are' : ' is'} currently inactive. You can update them from the edit panel.
            </div>
          )}
        </div>

      {editing && (
        <Modal
          isOpen={!!editing}
          onClose={cancel}
          title={editing === 'new' ? 'Create New Operational Unit' : 'Edit Unit Details'}
          size="md"
          confirmClose={form.name.trim() !== ''}
        >
          <div className="space-y-5">
            <Input
              label="Unit Name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Unit A"
              className="rounded-xl"
            />
            <Input
              label="Location"
              value={form.location}
              onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
              placeholder="e.g. Surat - Wing 1"
              className="rounded-xl"
            />
            <div className="grid grid-cols-2 gap-4">
              <Select
                label="Assign Supervisor"
                value={form.supervisorId}
                onChange={(e) => setForm((f) => ({ ...f, supervisorId: e.target.value }))}
                options={[
                  { label: 'Select Supervisor', value: '' },
                  ...supervisorOptions
                ]}
                className="rounded-xl"
              />
              <Select
                label="Status"
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                options={[
                  { value: 'active', label: 'Active' },
                  { value: 'inactive', label: 'Inactive' }
                ]}
                className="rounded-xl"
              />
            </div>

            <div className="p-4 bg-brand-50/50 border border-brand-100/50 rounded-2xl text-[11px] text-brand-700 leading-relaxed">
              <strong>Tip:</strong> Unit setup controls where operators, machines, and production activity are grouped. Ensure the name is unique for easy reporting.
            </div>

             <div className="pt-4">
              <Button onClick={save} disabled={acting || !form.name.trim()} className="w-full h-14 rounded-2xl font-bold shadow-lg shadow-brand-100 bg-brand-600 text-white">
                {acting ? 'Saving...' : (editing === 'new' ? 'Create Unit' : 'Save Changes')}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    <ConfirmDialog
        isOpen={!!deletingId}
        onClose={() => { setDeletingId(null); setError(''); }}
        onConfirm={handleDelete}
        title="Delete Unit?"
        message="This will permanently remove this production unit. This may impact associated records."
        confirmText="Yes, Delete"
        isLoading={acting}
        error={error}
      />
    </>
  )
}

export default UnitsPage
